import { notifications } from '@mantine/notifications';
import { IconLock, IconMoodPuzzled, IconTool } from '@tabler/icons-react';
import i18n from '@xver/i18n';

const CONNECTION_ERROR_ID = 'connection-error';
const MAINTENANCE_ID = 'maintenance';
const SESSION_EXPIRED_ID = 'session-expired';
const VISIBILITY_GRACE_MS = 15000;
const COOLDOWN_MS = 30000;

let lastVisibleAt = 0;
let lastShownAt = 0;
let lastMaintenanceShownAt = 0;

const isBrowser = typeof document !== 'undefined' && typeof window !== 'undefined';

if (isBrowser) {
    lastVisibleAt = document.visibilityState === 'visible' ? Date.now() : 0;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            lastVisibleAt = Date.now();
        }
    });
}

export interface NormalizedNetworkError {
    message: string;
    status: number;
    isNetworkError: true;
}

export function isNetworkError(error: unknown): error is NormalizedNetworkError {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as { isNetworkError?: boolean }).isNetworkError === true
    );
}

export function createNetworkError(): NormalizedNetworkError {
    return {
        message: i18n.t('connection.errorMessage', { ns: 'notifications' }),
        status: 0,
        isNetworkError: true,
    };
}

export function showConnectionErrorNotification(): void {
    if (!isBrowser) return;

    const now = Date.now();

    if (document.visibilityState !== 'visible') return;
    if (now - lastVisibleAt < VISIBILITY_GRACE_MS) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (now - lastShownAt < COOLDOWN_MS) return;

    lastShownAt = now;

    notifications.show({
        id: CONNECTION_ERROR_ID,
        title: i18n.t('connection.errorTitle', { ns: 'notifications' }),
        message: i18n.t('connection.errorMessage', { ns: 'notifications' }),
        icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
    });
}

export function showMaintenanceNotification(): void {
    if (!isBrowser) return;

    const now = Date.now();
    if (now - lastMaintenanceShownAt < COOLDOWN_MS) return;
    lastMaintenanceShownAt = now;

    notifications.show({
        id: MAINTENANCE_ID,
        title: i18n.t('maintenance.title', { ns: 'notifications' }),
        message: i18n.t('maintenance.message', { ns: 'notifications' }),
        icon: <IconTool size={32} color={'#f2994a'} />,
    });
}

export function showSessionExpiredNotification(): void {
    if (!isBrowser) return;

    notifications.show({
        id: SESSION_EXPIRED_ID,
        title: i18n.t('session.expiredTitle', { ns: 'notifications' }),
        message: i18n.t('session.expiredMessage', { ns: 'notifications' }),
        icon: <IconLock size={32} color={'#f2994a'} aria-hidden="true" />,
    });
}
